import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { Alert, Flex, Grid, H2, P, Tab, Tabs } from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import ConfiguredIntegrationsApi from '../../api/configured_integrations';
import ExecutionDetailsModal from '../../components/ExecutionDetails/ExecutionDetailsModal';
import TraceIdTab from '../../components/ManualExecution/TraceIdTab';
import LocationInfoTab from '../../components/ManualExecution/LocationInfoTab';
import { IntegrationDetailLocations } from './style';
import StepFunctionExecutionsApi from '../../api/step_function_executions';

export default function ManualInvocation({ integration }) {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [logModalDetailsVisibility, setLogModalDetailsVisibility] =
    useState(false);
  const [responseLog, setResponseLog] = useState(null);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Something Went Wrong');
  const [modalLoading, setModalLoading] = useState(false);
  const [invocationPayload, setInvocationPayload] = useState('');

  // Live execution status for the wizard animation in the modal
  const [executionStatus, setExecutionStatus] = useState(null);

  const fetchStepFunctionExecution = async (xDecisivTraceId) => {
    let currentTime = new Date();
    let keepTrying = true;
    const secondsBetweenRetries = 3;
    const secondsRetrying = 60;
    const delay = (s) =>
      // eslint-disable-next-line no-promise-executor-return
      new Promise((resolve) => setTimeout(resolve, s * 1000));

    const retryingExpiresAt = new Date();
    retryingExpiresAt.setSeconds(
      retryingExpiresAt.getSeconds() + secondsRetrying,
    );

    // Final statuses that indicate execution has completed
    const finalStatuses = [
      'SUCCEEDED',
      'FAILED',
      'NOT_FOUND',
      'ExecutionDoesNotExist',
      'BLOCKED_BY_CIRCUIT_BREAKER',
      'RE_ENQUEUED_BY_CIRCUIT_BREAKER',
      'BLOCKED_BY_CONDITION_RULE',
    ];

    // Set running status immediately
    setExecutionStatus({
      status: 'RUNNING',
      startDate: new Date().toISOString(),
      traceId: xDecisivTraceId,
      stateStatuses: null,
    });

    while (keepTrying && currentTime < retryingExpiresAt) {
      await delay(secondsBetweenRetries); // eslint-disable-line no-await-in-loop
      const response =
        // eslint-disable-next-line no-await-in-loop
        await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByTraceId(
          xDecisivTraceId,
          integration.id,
        ); // eslint-disable-line no-await-in-loop

      if (response.data && response.data.length > 0) {
        const { status } = response.data[0].attributes;

        setExecutionStatus({
          status,
          executionId: response.data[0].id,
          startDate: response.data[0].attributes.start_date,
          traceId: xDecisivTraceId,
          executionArn: response.data[0].attributes.execution_arn,
          stateStatuses: response.data[0].attributes.state_statuses || null,
        });

        // Check if execution has reached a final state
        if (finalStatuses.includes(status)) {
          keepTrying = false;
          // Carry the execution id so the modal can fetch its full payload from
          // GET /log-inspector/{id}; without it the payload section spins forever.
          setResponseLog({
            ...response.data[0].attributes,
            id: response.data[0].id,
          });
          setModalLoading(false);
          setButtonLoading(false);
          break;
        }
      }

      currentTime = new Date();
    }

    if (keepTrying) {
      setExecutionStatus((prev) =>
        prev ? { ...prev, status: 'TIMED_OUT' } : prev,
      );
      setDisplayAlert(true);
      setModalLoading(false);
      setLogModalDetailsVisibility(false);
      setButtonLoading(false);
      setAlertTitle(t`Something Went Wrong`);
      setAlertMessage(
        t`Could not fetch the Step Function Execution Information, you can check log-inspector page, the x-decisv-trace-id is ${xDecisivTraceId}`,
      );
    }
  };

  // Run the manual execution
  // eslint-disable-next-line consistent-return
  async function runManualInvocation(
    invocationInput,
    configuredIntegrationId,
    selectedTriggerType,
    srmAccountId,
    dealerId,
  ) {
    setDisplayAlert(false);
    setExecutionStatus(null);
    try {
      JSON.parse(invocationInput);
    } catch (e) {
      setDisplayAlert(true);
      setAlertTitle(t`Something Went Wrong`);
      setAlertMessage(t`Invalid Payload Format`);
      return false;
    }

    setButtonLoading(true);

    let invokePayload = JSON.parse(invocationInput);

    if (selectedTriggerType === 'http') {
      invokePayload = {
        data: {
          type: 'integration_invocation',
          attributes: JSON.parse(invocationInput),
          relationships: {
            configured_integration: {
              data: {
                id: configuredIntegrationId,
                type: 'configured_integrations',
              },
            },
          },
        },
      };
    } else {
      invokePayload.data.dealer_id = dealerId;
      invokePayload.data.srm_account_id = srmAccountId;
      invokePayload.metadata = {
        x_decisiv_trace_id: 'to-be-generated-by-backend',
        version: 1,
        origin: 'inf-ui.integration-test',
        created_at: new Date().toISOString(),
      };
    }

    const responseInvoke = await ConfiguredIntegrationsApi.invokeIntegration(
      configuredIntegrationId,
      JSON.stringify(invokePayload),
      'INTEGRATION_TEST',
    );

    if (responseInvoke.status === 201) {
      // Show modal immediately with loading state
      setInvocationPayload(JSON.stringify(invokePayload, null, 2));
      setModalLoading(true);
      setLogModalDetailsVisibility(true);

      await fetchStepFunctionExecution(responseInvoke.data.x_decisiv_trace_id);
    } else {
      setDisplayAlert(true);
      setButtonLoading(false);
      const errorTitle = responseInvoke?.errors[0]?.title;
      const errorMessage = responseInvoke?.errors[0]?.detail;
      setAlertTitle(errorTitle || t`Something Went Wrong`);
      setAlertMessage(
        errorMessage ||
          t`Something Went Wrong with the invocation and we can't determine a correct error message, please try again`,
      );
    }
  }

  return (
    <IntegrationDetailLocations
      textAlign="center"
      name="integrationDetailLocations"
    >
      <ExecutionDetailsModal
        log={responseLog}
        visibility={logModalDetailsVisibility}
        handleVisibility={setLogModalDetailsVisibility}
        logs={[]}
        onRefreshLogs={() => {}}
        loading={modalLoading}
        invocationPayload={invocationPayload}
        executionStatus={executionStatus}
        integrationName={integration?.attributes?.name}
      />
      <Flex
        name="manualInvocation"
        flexDirection="column"
        flexGrow={1}
        marginTop={2}
        marginLeft={2}
        marginRight={2}
      >
        {displayAlert && (
          <Grid.Column span="12">
            <Alert intent="danger" title={alertTitle}>
              {alertMessage}
            </Alert>
          </Grid.Column>
        )}
        <Flex name="manualInvocationForm" marginTop={2}>
          <Grid.Container>
            <Grid.Row>
              <Grid.Column span="12">
                <Flex name="titleHeader">
                  <Flex>
                    <H2 marginRight={2} marginTop={0.2}>
                      <Trans>Manual Execution</Trans>
                      <P shade={1} size="small">
                        <Trans>
                          Create a Manual Execution by defining a custom
                          Invocation Payload.
                        </Trans>
                      </P>
                    </H2>
                  </Flex>
                </Flex>
              </Grid.Column>
            </Grid.Row>
            <br />
            <Grid.Row>
              <Grid.Column span="12">
                <Tabs size="normal" defaultSelectedId="location-info-tab">
                  <Tab id="location-info-tab" title={t`Location Info`}>
                    <LocationInfoTab
                      buttonLoading={buttonLoading}
                      submitFunction={runManualInvocation}
                      integration={integration}
                    />
                  </Tab>
                  <Tab id="trace-id-tab" title={t`Trace-ID`}>
                    <TraceIdTab
                      buttonLoading={buttonLoading}
                      submitFunction={runManualInvocation}
                      integration={integration}
                    />
                  </Tab>
                </Tabs>
              </Grid.Column>
            </Grid.Row>
          </Grid.Container>
        </Flex>
      </Flex>
    </IntegrationDetailLocations>
  );
}

ManualInvocation.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
};
